import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { setImpersonationState, stashDeveloperSession } from '../../lib/session';
import { Button } from '../ui';

export function ImpersonatePanel({ active, isDeveloper }: { active: boolean; isDeveloper: boolean }) {
  const [users, setUsers] = useState<Array<{ id: string; display_name: string | null; handle: string | null; role: string }>>([]);
  const [loaded, setLoaded] = useState(false);

  async function load() {
    const { data: ms } = await supabase
      .from('profiles')
      .select('id, display_name, handle, role')
      .order('created_at', { ascending: true });
    // Non-developer admins may only impersonate regular members - the
    // server enforces this too (impersonate.ts target-role guard), but
    // filter the list so we don't show targets that would 403.
    const visible = (ms ?? []).filter((u: any) => isDeveloper || u.role === 'user');
    setUsers(visible as any);
    setLoaded(true);
  }

  useEffect(() => {
    if (active && !loaded) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return (
    <>
      <div className="admin__panel-head">
        <h2>Impersonate a member</h2>
        <span className="admin__count">{users.length} members</span>
      </div>
      <p className="admin__note">
        Sign in as any member to see their exact view. All actions during an impersonation
        session are written to <code>impersonation_log</code>.
      </p>
      <ul className="admin__posts">
        {users.map((m) => (
          <li key={m.id} className="admin__post">
            <div className="admin__post-meta">
              <h3>{m.display_name || m.handle || m.id}</h3>
              <p className="admin__post-stats">{m.role} · {m.handle ? `@${m.handle}` : 'no handle'}</p>
            </div>
            <div className="admin__post-actions">
              <ImpersonateButton targetId={m.id} targetName={m.display_name || m.handle || 'user'} />
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

function ImpersonateButton({ targetId, targetName }: { targetId: string; targetName: string }) {
  const [busy, setBusy] = useState(false);

  async function go() {
    setBusy(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      const refresh = sess.session?.refresh_token;
      if (!token || !refresh) throw new Error('No session');

      // Stash developer session so "Exit" can restore it.
      stashDeveloperSession({ access_token: token, refresh_token: refresh });

      const res = await fetch('/api/admin/impersonate', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ target_user_id: targetId, reason: 'Manual impersonation from /admin' }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error ?? 'Impersonation denied.');
      }
      const { session_id, token_hash, target_display_name } = await res.json();

      // Redeem the one-time hashed token as a session swap. This signs the
      // browser in AS the target user without sending an email. When verifying
      // a hashed token, supabase-js requires ONLY token_hash + type - passing
      // `email` here triggers "Only the token_hash and type should be provided".
      const { error } = await supabase.auth.verifyOtp({
        token_hash,
        type: 'magiclink',
      });
      if (error) throw error;

      setImpersonationState({
        active: true,
        session_id,
        target_display_name: target_display_name || targetName,
      });
      window.location.assign('/');
    } catch (err: any) {
      alert(err?.message ?? 'Impersonation failed.');
      setBusy(false);
    }
  }

  return (
    <Button type="button" variant="secondary" size="sm" onClick={go} disabled={busy}>
      {busy ? '…' : 'Sign in as'}
    </Button>
  );
}
